import { useEffect, useRef } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { patchLiveDeviceState } from 'store/device.slice';

/**
 * Opens an SSE connection to /api/device/live-control-room-sse and patches
 * live sensor-derived fields (speed, RPM, ignition, derived_state, etc.) into
 * latestOperationalData for each device as messages arrive.
 *
 * Athena-computed fields (utilization, trips, tonnage) are NOT overwritten —
 * those continue to come from the 5-min operational-data poll.
 */
export function useControlRoomSSE() {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((s) => s.authReducer.accessToken);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const esRef = useRef<InstanceType<typeof EventSourcePolyfill> | null>(null);

  useEffect(() => {
    if (!accessToken || !customerId) return;

    const base = process.env.REACT_APP_MANAGEMENT_API_URL ?? '';
    const url = `${base}/api/device/live-control-room-sse?customerId=${customerId}`;

    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    esRef.current = es;

    es.onmessage = function (e: any) {
      try {
        const data = JSON.parse(e.data as string);

        // Heartbeat — ignore
        if (data?.sse_heartbeat) return;
        // Connection message — ignore
        if (data?.message) return;
        // Error from server — log only
        if (data?.error) {
          console.warn('[control-room-sse] server error:', data.error);
          return;
        }

        const { deviceId, ...liveState } = data;
        if (!deviceId) return;

        dispatch(patchLiveDeviceState({ deviceId, liveState }));
      } catch (err) {
        console.error('[control-room-sse] parse error:', err);
      }
    };

    es.onerror = () => {
      // EventSourcePolyfill auto-reconnects — no action needed
    };

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [accessToken, customerId]);
}
